import { useRef, useState, useEffect } from "react";
import {
  Bus, ListChecks, User, CalendarDays, Send,
  Check, ChevronRight, ChevronDown, Plus, Lock, ShieldCheck, IdCard,
} from "lucide-react";
import { useReducedMotion } from "framer-motion";
import { Reveal } from "@/lib/reveal";

const CREATE = {
  n: 1, id: "create", title: "Create your A-ID", Icon: IdCard, color: "#00D2B4",
  desc: "Set up your accessibility identity once — your information, ready whenever you need it.",
};

const STEPS = [
  { n: 2, id: "journey", title: "Choose a journey", Icon: Bus, color: "#00D2B4",
    desc: "Select the journey relevant to your current situation." },
  { n: 3, id: "information", title: "Choose information", Icon: ListChecks, color: "#00F0FF",
    desc: "Choose the information that's relevant and helpful to share." },
  { n: 4, id: "who", title: "Choose who", Icon: User, color: "#3B82F6",
    desc: "Choose who you want to share this information with." },
  { n: 5, id: "access", title: "Set access", Icon: CalendarDays, color: "#8B5CF6",
    desc: "Set how long they can access the information you've shared." },
  { n: 6, id: "share", title: "Share", Icon: Send, color: "#10B981",
    desc: "Review and share only what you've chosen." },
];

const CHECKS = [
  { label: "Wheelchair access", on: true },
  { label: "Assistance required", on: true },
  { label: "Boarding support", on: true },
  { label: "Seat preference", on: false },
];

const ACCESS = [
  { label: "24 hours", on: true },
  { label: "7 days", on: false },
  { label: "Custom", on: false, chevron: true },
];

const GRADIENT = "linear-gradient(90deg, #00D2B4, #00F0FF 30%, #3B82F6 55%, #8B5CF6 78%, #10B981)";

const Pill = ({ children, chevron }) => (
  <div className="flex items-center justify-between gap-2 rounded-lg border border-white/12 bg-white/[0.04] px-3 py-2.5 text-sm text-slate-200">
    <span className="truncate">{children}</span>
    {chevron === "down" ? <ChevronDown className="h-4 w-4 shrink-0 text-slatemuted" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slatemuted" />}
  </div>
);

const StepMini = ({ id }) => {
  if (id === "journey") {
    return (
      <div className="mt-4 space-y-3">
        <Pill chevron="down">Transport</Pill>
        <div className="flex items-center gap-1.5">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="h-1.5 w-1.5 rounded-full" style={{ background: i === 1 ? "#00D2B4" : "rgba(148,163,184,0.35)" }} />
          ))}
        </div>
      </div>
    );
  }
  if (id === "information") {
    return (
      <ul className="mt-4 space-y-2.5">
        {CHECKS.map((c) => (
          <li key={c.label} className="flex items-center gap-2.5 text-sm text-slate-200">
            <span
              className="flex h-5 w-5 items-center justify-center rounded-[6px] border"
              style={{ borderColor: c.on ? "#00D2B4" : "rgba(255,255,255,0.2)", background: c.on ? "#00D2B4" : "transparent" }}
            >
              {c.on && <Check className="h-3.5 w-3.5 text-navy-900" />}
            </span>
            {c.label}
          </li>
        ))}
      </ul>
    );
  }
  if (id === "who") {
    return (
      <div className="mt-4 space-y-3">
        <Pill>Airline / Transport Provider</Pill>
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-teal">
          <Plus className="h-4 w-4" /> Add another
        </span>
      </div>
    );
  }
  if (id === "access") {
    return (
      <ul className="mt-4 space-y-2.5">
        {ACCESS.map((a) => (
          <li
            key={a.label}
            className="flex items-center justify-between rounded-lg border px-3 py-2.5 text-sm"
            style={{ borderColor: a.on ? "#8B5CF6" : "rgba(255,255,255,0.12)", background: a.on ? "rgba(139,92,246,0.12)" : "transparent" }}
          >
            <span className="flex items-center gap-2.5 text-slate-200">
              <span className="flex h-4 w-4 items-center justify-center rounded-full border" style={{ borderColor: a.on ? "#8B5CF6" : "rgba(255,255,255,0.3)" }}>
                {a.on && <span className="h-2 w-2 rounded-full bg-[#8B5CF6]" />}
              </span>
              {a.label}
            </span>
            {a.on ? <Check className="h-4 w-4 text-[#8B5CF6]" /> : a.chevron ? <ChevronRight className="h-4 w-4 text-slatemuted" /> : null}
          </li>
        ))}
      </ul>
    );
  }
  // share
  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between rounded-full bg-teal px-4 py-2.5 text-sm font-semibold text-navy-900">
        Share now <ChevronRight className="h-4 w-4" />
      </div>
      <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-teal">
        <Lock className="h-3.5 w-3.5" /> You&apos;re in control
      </span>
    </div>
  );
};

const NumberNode = ({ s, size = "h-11 w-11", text = "text-base" }) => (
  <span
    className={`relative flex ${size} items-center justify-center rounded-full bg-navy-900 font-display ${text} font-bold`}
    style={{ color: s.color, boxShadow: `0 0 0 1.5px ${s.color}, 0 0 26px -6px ${s.color}` }}
  >
    <span className="absolute -inset-1 rounded-full border border-dashed animate-spin-slower" style={{ borderColor: `${s.color}30` }} aria-hidden="true" />
    {s.n}
  </span>
);

const IconTile = ({ s, className = "" }) => (
  <span
    className={`flex h-14 w-14 items-center justify-center rounded-2xl border ${className}`}
    style={{
      borderColor: `${s.color}40`,
      background: `radial-gradient(circle at 30% 20%, ${s.color}26, rgba(10,19,34,0.4) 75%)`,
      boxShadow: `0 0 26px -10px ${s.color}`,
    }}
  >
    <s.Icon className="h-7 w-7" style={{ color: s.color }} aria-hidden="true" />
  </span>
);

export const SharingFlow = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || revealed) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { rootMargin: "-120px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [revealed]);
  const shown = reduce || revealed;

  return (
    <section className="relative pt-6 pb-20 lg:pt-10 lg:pb-32 overflow-hidden" data-testid="sharing-flow-section">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/4 top-1/3 h-[420px] w-[520px] rounded-full bg-aigablue/10 blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={shown ? "reveal-in" : ""}>
          {/* Step 1 — Create your A-ID (feeds into the sharing flow below) */}
          <div className="flex flex-col items-center">
            <div
              className="reveal-item relative flex w-full max-w-md items-center gap-4 rounded-2xl border p-5 backdrop-blur-sm"
              style={{
                borderColor: `${CREATE.color}33`,
                background: `linear-gradient(150deg, ${CREATE.color}14, rgba(10,19,34,0.4) 72%)`,
                "--reveal-from": "translateY(22px)",
                "--reveal-delay": "80ms",
              }}
              aria-label={`Step ${CREATE.n}: ${CREATE.title}. ${CREATE.desc}`}
              data-testid="sharing-step-1"
            >
              <NumberNode s={CREATE} size="h-14 w-14" text="text-lg" />
              <IconTile s={CREATE} className="h-11 w-11 rounded-xl [&_svg]:h-6 [&_svg]:w-6" />
              <div className="min-w-0">
                <h3 className="font-display text-lg font-semibold text-white">{CREATE.title}</h3>
                <p className="mt-1 text-sm text-slate-300 leading-relaxed">{CREATE.desc}</p>
              </div>
            </div>
            {/* downward connector into the flow */}
            <span
              className="reveal-scaley mt-2 h-8 w-[2px] rounded-full"
              style={{ background: `linear-gradient(180deg, ${CREATE.color}, rgba(0,240,255,0.5))`, "--reveal-delay": "220ms" }}
              aria-hidden="true"
            />
            <ChevronDown className="reveal-item h-5 w-5 text-teal" style={{ "--reveal-delay": "300ms" }} aria-hidden="true" />
          </div>

          {/* desktop: connected horizontal pathway */}
          <div className="hidden lg:block">
            <div className="relative mt-16">
              {/* the system surface */}
              <ol
                className="grid grid-cols-5 rounded-3xl border border-white/10 backdrop-blur"
                style={{ background: "linear-gradient(180deg, rgba(14,28,51,0.55), rgba(10,19,34,0.35))" }}
              >
                {STEPS.map((s, i) => (
                  <li
                    key={s.id}
                    className={`relative px-5 pt-14 pb-7 reveal-item ${i < STEPS.length - 1 ? "border-r border-white/[0.06]" : ""}`}
                    aria-label={`Step ${s.n}: ${s.title}. ${s.desc}`}
                    data-testid={`sharing-step-${s.n}`}
                    style={{ "--reveal-from": "translateY(22px)", "--reveal-delay": `${150 + i * 90}ms` }}
                  >
                    <IconTile s={s} />
                    <h3 className="mt-4 font-display text-lg font-semibold text-white">{s.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-300 leading-relaxed">{s.desc}</p>
                    <StepMini id={s.id} />
                  </li>
                ))}
              </ol>

              {/* node rail straddling the top edge */}
              <div className="pointer-events-none absolute inset-x-0 top-0 z-10 -translate-y-1/2 px-[10%]">
                <div className="relative grid grid-cols-5">
                  <span
                    className="absolute left-[10%] right-[10%] top-[calc(50%-1px)] h-[2px] rounded-full reveal-scalex"
                    style={{ background: GRADIENT }}
                    aria-hidden="true"
                  />
                  <span
                    className="absolute left-[10%] right-[10%] top-[calc(50%-3px)] h-[6px] rounded-full blur-md animate-glow-breathe reveal-scalex"
                    style={{ background: GRADIENT }}
                    aria-hidden="true"
                  />
                  {STEPS.map((s, i) => (
                    <div key={s.id} className="flex justify-center">
                      <div className="reveal-item" style={{ "--reveal-from": "scale(0.35)", "--reveal-delay": `${300 + i * 130}ms` }}>
                        <NumberNode s={s} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* mobile / tablet: vertical connected timeline */}
          <div className="mt-6 lg:hidden">
            <ol className="relative space-y-5 pl-1">
              <span
                className="pointer-events-none absolute left-[28px] top-8 bottom-8 w-[2px] rounded-full reveal-scaley"
                style={{ background: "linear-gradient(180deg, #00D2B4, #00F0FF 25%, #3B82F6 55%, #8B5CF6 80%, #10B981)" }}
                aria-hidden="true"
              />
              {STEPS.map((s, i) => (
                <li
                  key={s.id}
                  className="relative flex items-start gap-4 reveal-item"
                  aria-label={`Step ${s.n}: ${s.title}. ${s.desc}`}
                  data-testid={`sharing-step-${s.n}-m`}
                  style={{ "--reveal-delay": `${100 + i * 100}ms` }}
                >
                  <NumberNode s={s} size="h-14 w-14" text="text-lg" />
                  <div
                    className="flex-1 rounded-2xl border p-5 backdrop-blur-sm"
                    style={{ borderColor: `${s.color}2e`, background: `linear-gradient(150deg, ${s.color}12, rgba(10,19,34,0.4) 72%)` }}
                  >
                    <div className="flex items-center gap-3">
                      <IconTile s={s} className="h-11 w-11 rounded-xl [&_svg]:h-6 [&_svg]:w-6" />
                      <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
                    </div>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{s.desc}</p>
                    <StepMini id={s.id} />
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* manage / control banner */}
        <Reveal delay={0.1}>
          <div
            className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-2xl border border-teal/25 p-5 backdrop-blur-sm"
            style={{ background: "linear-gradient(120deg, rgba(0,210,180,0.08), rgba(10,19,34,0.4) 60%)" }}
            data-testid="sharing-manage-banner"
          >
            <div className="flex items-center gap-4">
              <span
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-teal/40"
                style={{ background: "radial-gradient(circle, rgba(0,210,180,0.25), transparent 70%)", boxShadow: "0 0 22px -8px #00D2B4" }}
              >
                <ShieldCheck className="h-6 w-6 text-teal" aria-hidden="true" />
              </span>
              <div>
                <p className="font-display text-base font-semibold text-white">You can update, pause or remove access at any time.</p>
                <p className="text-sm text-slate-300">You&apos;re always in control.</p>
              </div>
            </div>
            <span className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white">
              Manage sharing <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </div>
        </Reveal>

        <div className="mt-14 flex items-center justify-center gap-4" aria-hidden="true">
          <span className="h-px w-16 bg-white/10" />
          <span className="text-xs font-mono uppercase tracking-[0.24em] text-slatemuted">
            One identity · Many journeys · Your control
          </span>
          <span className="h-px w-16 bg-white/10" />
        </div>
      </div>
    </section>
  );
};
